/* eslint-disable max-lines */
import {endOfDay, isToday, isWithinInterval, startOfDay, subDays} from 'date-fns';
import itiriri from 'itiriri';
import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch, useRouteMatch } from 'react-router-dom';
import {
  HSAdministeredAdHocDrug,
  HSAdministeredDrug,
  HSDoseRound,
  HSDrug,
  HSPackedMedication,
  HSPackedPrnMedication,
  HSPatient,
  HSTestResult,
  ListSecondCheckSettingDto,
  NimAvailableDrugDto,
} from 'server-openapi';
import styled from 'styled-components';
import urljoin from 'url-join';
import { useCurrentUser } from '../../../../core/authn/UserProvider';
import { Layout } from '../../../../kit/Layout';
import { useSyncCenter } from '../../../../syncstream/SyncCenterProvider';
import { useStore } from '../../../../core/storage/hooks/UseStore';
import { useApiUtils } from '../../../../syncstream/utils/hooks/useApiUtils';
import { RoundScheduleItem } from '../../../../syncstream/utils/RoundUtils';
import { IDrugDetailProps } from '../drugDetails/DrugDetailDialog';
import { DrugOutcome } from '../medicationInformation/DrugOutcomeDialog';
import { DrugAdministrationUtils } from './DrugAdministrationUtils';
import { ContinuousMedicationList } from './TabLists/ContinuousMedicationList';
import { NIMsMedicationList } from './TabLists/NIMsMedicationList';
import { PatchesMedicationList } from './TabLists/PatchesMedicationList';
import { PRNsMedicationList } from './TabLists/PRNsMedicationList';
import { ShortCoursesMedicationList } from './TabLists/ShortCoursesMedicationList';
import { SyringeDriversMedicationList } from './TabLists/SyringeDriversMedicationList';
import { toasts } from '../../../../kit/Toasts/Toaster';
import { DateUtils } from '../../../../core/utils/dateUtils';
import { useRoundSchedule } from '../../../Rounds/services/RoundScheduleProvider';
import { EnqueuedDrugCreateData } from '../../../../syncstream/SyncRounds';
import {useGroupPermissions} from "../../../../core/authz/PermissionsProvider";
import {useGetSyringeScheduleFromParameters} from "../../../../syncstream/utils/hooks/GetSyringeDriverSchedule";
import {useGetPatchScheduleFromParameters} from "../../../../syncstream/utils/hooks/GetPatchSchedule";

interface IProps {
  patient: HSPatient;
  date: Date;
  facilityGroupId: number;
  openDrugDetail: (props: IDrugDetailProps) => void;
  round?: HSDoseRound;
  roundId?: string;
}

export interface AdministrationPermissions {
  canCompleteRound: boolean;
  canCreateTestResults: boolean;
  canAdministerS8Medication: boolean;
  canAdministerControlledDrugs: boolean;
  canAdministerPackedControlledDrugs: boolean;
  packedMedsDoNotNeedSecondCheck: boolean;
  canAdministerMedication: boolean;
  canAdministerMissedMedication: boolean;
  canReAdministerMedication: boolean;
  canAdministerS4Medication: boolean;
  canAdministerShortCourseMedication: boolean;
  canAdministerPRNAndRecordItsOutcome: boolean;
  canAdministerNIMAndRecordItsOutcome: boolean;
  canAdministerNonPackedMedicationExcludingInjectable: boolean;
  canAdministerNonPackedMedicationIncludingInjectable: boolean;
  canAdministerSecondCheckMedication: boolean;
  canAdministerPackedMedication: boolean;
  canAdministerPatchMedicationExcludingS8: boolean;
  canAdministerPatchMedicationIncludingS8: boolean;
  canOperateSyringeDriver: boolean;
  canAdministerSlidingScaleMedication: boolean;
  canVewResupplyOrders: boolean;
  canWithholdMedication: boolean;
  canAdministerPackedMedicationIndividually: boolean;
  canSetOtherDoseStatuses: boolean;
  canSightPatch: boolean;
  canRecordNote: boolean;
}

//eslint-disable-next-line sonarjs/cognitive-complexity, max-lines-per-function
export function MedicationListsTabbedRouter(props: IProps) {
  const { patient, facilityGroupId, date, openDrugDetail, round } = props;

  const [secondCheckableMedication, setSecondCheckableMedication] = useState<{[id:number]: SecondCheckableMedication}>({});

  const user = useCurrentUser();
  const { path } = useRouteMatch();
  const groupPermissions = useGroupPermissions();
  const administrationPermissions: AdministrationPermissions = {
    canCompleteRound: groupPermissions.canCompleteRounds ?? false,
    canCreateTestResults: groupPermissions.canRecordVitals ?? false,
    canAdministerS8Medication: groupPermissions.canAdministerS8Medication ?? false,
    canAdministerControlledDrugs: groupPermissions.canAdministerControlledDrugs ?? false,
    canAdministerPackedControlledDrugs: groupPermissions.canAdministerPackedControlledDrugs ?? false,
    packedMedsDoNotNeedSecondCheck: groupPermissions.packedMedsDoNotNeedSecondCheck ?? false,
    canAdministerMedication: groupPermissions.canAdministerMedication ?? false,
    canAdministerMissedMedication: groupPermissions.canAdministerMissedMedication ?? false,
    canReAdministerMedication: groupPermissions.canReAdministerMedication ?? false,
    canAdministerS4Medication: groupPermissions.canAdministerS4Medication ?? false,
    canAdministerShortCourseMedication: groupPermissions. canAdministerShortCourseMedication ?? false,
    canAdministerPRNAndRecordItsOutcome: groupPermissions.canAdministerPRNAndRecordItsOutcome ?? false,
    canAdministerNIMAndRecordItsOutcome: groupPermissions.canAdministerNIMAndRecordItsOutcome ?? false,
    canAdministerNonPackedMedicationExcludingInjectable: groupPermissions.canAdministerNonPackedMedicationsExcludingInjectableMedication ?? false,
    canAdministerNonPackedMedicationIncludingInjectable: groupPermissions.canAdministerNonPackedMedicationsIncludingInjectableMedication ?? false,
    canAdministerSecondCheckMedication: groupPermissions.canAdministerDoubleSigningMedication ?? false,
    canAdministerPackedMedication: groupPermissions.canAdministerPackedMedications ?? false,
    canAdministerPatchMedicationExcludingS8: groupPermissions.canAdministerPatchMedicationExcludingS8 ?? false,
    canAdministerPatchMedicationIncludingS8: groupPermissions.canAdministerS8PatchMedication ?? false,
    canOperateSyringeDriver: groupPermissions.canOperateSyringeDriverMedication ?? false,
    canAdministerSlidingScaleMedication: groupPermissions.canAdministerSlidingScaleMedication ?? false,
    canVewResupplyOrders: groupPermissions.canViewResupplyOrder ?? false,
    canWithholdMedication: groupPermissions.canWithholdMedication ?? false,
    canAdministerPackedMedicationIndividually: groupPermissions.canAdministerPackedMedicationIndividually ?? false,
    canSetOtherDoseStatuses: groupPermissions.canSetOtherDoseStatuses ?? false,
    canSightPatch: groupPermissions.canSightPatch ?? false,
    canRecordNote: groupPermissions.canRecordNote ?? false
  };
  const apiUtils = useApiUtils();
  const services = useSyncCenter();
  const drugStore = useStore(services.drugs.store).store;
  const secondCheckSettingsStore = useStore(services.secondCheckSettings.store).store;
  const facilitiesStore = useStore(services.facilities.store).store;

  const drugs = itiriri(drugStore.values()).toArray();

  const secondCheckSettings = secondCheckSettingsStore.get(facilityGroupId.toString());
  const roundScheduleContext = useRoundSchedule();
  React.useEffect(() => {
    if (!roundScheduleContext.roundSchedule) {
      roundScheduleContext.set(round);
    }
  }, [props.roundId]);
  const roundSchedule = round ? roundScheduleContext.roundSchedule : undefined;
  const packedPatientDay = apiUtils.patients.findPackedPatientDay(patient.hsId!, date, patient.facility);
  const previousPackedPatientDay = apiUtils.patients.findPackedPatientDay(
    patient.hsId!,
    subDays(date, 1),
    patient.facility,
  );

  const facilityIds = itiriri(facilitiesStore.values())
      .filter((f) => f.facilityGroupId === +facilityGroupId)
      .map((f) => f.hsId!)
      .toArray();

  const lastTwoPackedPatientDaysMedication = [
    ...(packedPatientDay?.packedMedications ?? []),
    ...(previousPackedPatientDay?.packedMedications ?? []),
  ];

  const interval = round
    ? apiUtils.rounds.getRoundWindow(round.createdAt ? DateUtils.toDate(round.createdAt) : new Date(), facilityGroupId)
    : undefined;

  const ceasedMedicationInARound = interval
    ? lastTwoPackedPatientDaysMedication.filter(
        (packedMed) =>
          packedMed.interimDeleted &&
          isWithinInterval(DateUtils.toDate(packedMed.doseTimestamp!), {
            start: DateUtils.toDate(interval.start),
            end: DateUtils.toDate(interval.end),
          }),
      )
    : [];

  const today = {
    start: DateUtils.fromDate(startOfDay(new Date())), 
    end: DateUtils.fromDate(endOfDay(new Date()))
  };
  
  const getSyringeScheduleFromParameters = useGetSyringeScheduleFromParameters();
  const getPatchScheduleFromParameters = useGetPatchScheduleFromParameters();
  
  // When you have a syringe driver that crosses a day boundary, need to include the packed medication that it came
  // from.
  const getPackedMedications = (): HSPackedMedication[] => {
    const patientMap: { [id:number]:HSPackedMedication} = {};
    const inProgressSyringeDrivers = getSyringeScheduleFromParameters(today, facilityIds);
    if (packedPatientDay?.packedMedications) {
      for (const pm of packedPatientDay.packedMedications) {
        patientMap[pm.hsId!] = pm;
      }
    }
    for (const sd of inProgressSyringeDrivers) {
      if (sd.packedMedication && !patientMap[sd.packedMedication.hsId!]) {
        patientMap[sd.packedMedication.hsId!] = sd.packedMedication;
      }
    }

    const inProgressPatchApplications = getPatchScheduleFromParameters(today, facilityIds);
    if (packedPatientDay?.packedMedications) {
      for (const pm of packedPatientDay.packedMedications) {
        patientMap[pm.hsId!] = pm;
      }
    }
    for (const pa of inProgressPatchApplications) {
      if (pa.packedMedication && !patientMap[pa.packedMedication.hsId!]) {
        patientMap[pa.packedMedication.hsId!] = pa.packedMedication;
      }
    }
    return Object.values(patientMap);
  }
  
  const viewableMedication =
    roundSchedule && isToday(date)
      ? [
          ...roundSchedule.filter((s) => s.patient.hsId === patient.hsId).map((s) => s.packedMedication),
          ...ceasedMedicationInARound,
        ]
      : getPackedMedications();

  // this is the list of drugs that appears on the resident details page
  const groupedMedications =
    viewableMedication &&
    apiUtils.residentDetails.getGroupedMedications(
      viewableMedication,
      patient.patientProfiles ?? [],
      date,
      props.patient.hsId!,
      props.patient.facility!,
    );

  useEffect(() => {
    const updatedSecondCheckMedicationList = apiUtils.residentDetails
      .getSecondCheckableMedication(
        facilityGroupId,
        patient,
        lastTwoPackedPatientDaysMedication, [],
        administrationPermissions.packedMedsDoNotNeedSecondCheck,
        roundSchedule,
      )
      .filter((med) => {
        return !secondCheckableMedication[med.id];
      });

    const secondCheckableHash = updatedSecondCheckMedicationList.reduce((hsh, entry) => {
      hsh[entry.id] = entry;
      return hsh;
    }, {} as {[id: number]: SecondCheckableMedication});
    setSecondCheckableMedication({...secondCheckableMedication, ...secondCheckableHash});
  }, [roundSchedule, date]);

  const previousDosedDrugs = groupedMedications
    ? apiUtils.residentDetails.getPreviousDosedDrugsForPackedPatientMedications(
        groupedMedications?.flatMap((group) => [
          ...group.medications,
          ...[],
        ]),
      )
    : [];

  const onAddOutcome = async (
    outcome: DrugOutcome,
    administeredDrugId: string,
    doseRoundClinicalSystemId: string,
    isAdHoc: boolean,
  ) => {
    if (!administrationPermissions.canAdministerPRNAndRecordItsOutcome) {
      toasts.error('You do not have permission to record PRN outcomes');
      return;
    }
    if (outcome.testResults.length > 0 && !administrationPermissions.canCreateTestResults) {
      toasts.error('You do not have permission to record vitals');
      return;
    }
    await DrugAdministrationUtils.addOutcome(
      outcome,
      administeredDrugId,
      doseRoundClinicalSystemId,
      services,
      user.profile.sub,
      patient,
      facilityGroupId,
      isAdHoc,
    );
  };

  const onAdministerDrugs = async (
    drugs: (HSAdministeredDrug | HSAdministeredAdHocDrug)[],
    timestamp?: string,
    testResults?: HSTestResult[],
  ): Promise<EnqueuedDrugCreateData | undefined> => {
    if (!round && !administrationPermissions.canCompleteRound) {
      toasts.error('You do not have permission to complete a temporary round');
      return;
    }

    return await DrugAdministrationUtils.administerDrug(
      user.profile.sub,
      drugs,
      patient,
      facilityGroupId,
      services.rounds.service,
      services.testResults.service,
      round,
      timestamp,
      testResults,
    );
  };

  const medicationListProps: MedicationListProps = {
    currentRound: round,
    patient: patient,
    roundSchedule: roundSchedule ?? [],
    facilityGroupId: facilityGroupId,
    previousDosedDrugs: previousDosedDrugs,
    drugList: drugs,
    administrationPermissions: administrationPermissions,
    openDrugDetail: openDrugDetail,
    selectedDate: props.date,
    secondCheckData: {
      secondCheckSettings: secondCheckSettings,
      secondCheckableMedication: secondCheckableMedication,
      setMedicationSecondChecked: (med: SecondCheckableMedication) => {
        const val: {[id:string]: SecondCheckableMedication} = {};
        val[med.id] = med;
        
        setSecondCheckableMedication({...secondCheckableMedication, ...val});
      },
    },
  };

  return (
    <Switch>
      <Redirect exact path={path} to={urljoin(path, 'continuous')} />
      <MedicationListWrapper>
        <Route
          exact
          path={urljoin(path, 'prns')}
          render={() => (
            <PRNsMedicationList
              {...medicationListProps}
              groupedPackedMedicationList={groupedMedications!}
              onAddOutcome={onAddOutcome}
              onAdministerDrugs={async (drugs, doseTimestamp, testResults) =>
                onAdministerDrugs(drugs, doseTimestamp, testResults)
              }
            />
          )}
        />
        <Route
          exact
          path={urljoin(path, 'nims')}
          render={() => (
            <NIMsMedicationList
              {...medicationListProps}
              groupedPackedMedicationList={groupedMedications!}
              onAdministerAdHoc={async (drug) => onAdministerDrugs(drug)}
              onAddOutcome={onAddOutcome}
            />
          )}
        />
        {groupedMedications && (
          <>
            <Route
              exact
              path={urljoin(path, 'continuous')}
              render={() => (
                <ContinuousMedicationList
                  {...medicationListProps}
                  onAdministerDrugs={async (drugs, timestamp, testResults) =>
                    onAdministerDrugs(drugs, timestamp, testResults)
                  }
                  groupedPackedMedicationList={groupedMedications}
                  patientId={patient.hsId!}
                  isToday={isToday(date)}
                  lastTwoPackedPatientDaysMedication={lastTwoPackedPatientDaysMedication}
                />
              )}
            />
            <Route
              exact
              path={urljoin(path, 'syringe-drivers')}
              render={() => (
                <SyringeDriversMedicationList
                  {...medicationListProps}
                  onAdministerDrugs={async (drugs, timestamp) => onAdministerDrugs(drugs, timestamp)}
                  groupedPackedMedicationList={groupedMedications}
                  lastTwoPackedPatientDaysMedication={lastTwoPackedPatientDaysMedication}
                  displayAllActions
                  displayAllActivity={true}
                />
              )}
            />
            <Route
              exact
              path={urljoin(path, 'patches')}
              render={() => (
                <PatchesMedicationList
                  {...medicationListProps}
                  onAdministerDrugs={async (adrugs, timestamp) => onAdministerDrugs(adrugs, timestamp)}
                  groupedPackedMedicationList={groupedMedications}
                  patientId={patient.hsId!}
                  displayAllActions={true}
                />
              )}
            />
            <Route
              exact
              path={urljoin(path, 'short-course')}
              render={() => (
                <ShortCoursesMedicationList
                  {...medicationListProps}
                  onAdministerDrugs={async (adrugs, timestamp, testResults) =>
                    onAdministerDrugs(adrugs, timestamp, testResults)
                  }
                  groupedPackedMedicationList={groupedMedications}
                />
              )}
            />
          </>
        )}
      </MedicationListWrapper>
    </Switch>
  );
}
interface PreviousDosedDrugs {
  mostRecentDosedDrugs: HSAdministeredDrug[];
  packedMedication: HSPackedMedication;
}

export interface MedicationListProps {
  currentRound?: HSDoseRound;
  patient: HSPatient;
  roundSchedule?: RoundScheduleItem[];
  facilityGroupId: number;
  previousDosedDrugs?: PreviousDosedDrugs[];
  drugList?: HSDrug[];
  administrationPermissions: AdministrationPermissions;
  secondCheckData?: SecondCheckData;
  openDrugDetail?: (props: IDrugDetailProps) => void;
  selectedDate: Date;
}

// TODO: what is the purpose of this?
export interface SecondCheckableMedication {
  medication: HSPackedMedication | HSPackedPrnMedication | NimAvailableDrugDto;
  id: number;
  hsId?: number;
  userName?: string;
  date?: Date;
}

export interface SecondCheckData {
  secondCheckableMedication: {[id:number]: SecondCheckableMedication};
  secondCheckSettings?: ListSecondCheckSettingDto;
  setMedicationSecondChecked: (secondCheckableMedication: SecondCheckableMedication) => void;
}

const MedicationListWrapper = styled(Layout)`
  height: calc(100% - 10px);
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;
